import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { useCompany } from './CompanyContext';
import { useUser } from './UserContext'; // Import useUser
import FilterToolbar from './FilterToolbar'; // Import the toolbar
import { formatDate } from './utils/dateUtils';
import { debounce } from 'lodash';

const API_URL = '/api/requisiciones';

const statusColors = {
  'Pendiente': 'warning',
  'Aprobada': 'success',
  'Cancelado': '#000000',
  'Terminado': '#FFC107',
};

const REQUISICION_STATUSES = [
  { value: 'Pendiente', label: 'Pendiente' },
  { value: 'Aprobada', label: 'Aprobada' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'terminada', label: 'Terminada' },
];

// Define a default set of filters
const getDefaultFilters = () => {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return {
    q: '',
    startDate: thirtyDaysAgo.toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    statuses: ['Pendiente', 'Aprobada'], // Default to active statuses
  };
};

const highlightText = (text, query) => {
  if (!query || !text) {
    return text;
  }
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <span>
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index}>{part}</mark>
        ) : (
          part
        )
      )}
    </span>
  );
};

function RequisicionList() {
  const [requisiciones, setRequisiciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(getDefaultFilters());
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompanyIds } = useCompany();
  const { user } = useUser(); // Get user from context

  const canCreate = user && ['USER_A', 'USER_B', 'ADMIN', 'SUPERADMIN'].includes(user.role);
  const showFilters = user && ['USER_A', 'USER_B', 'ADMIN', 'SUPERADMIN'].includes(user.role);

  useEffect(() => {
    const fetchRequisiciones = debounce(async (currentFilters, companyIds, currentSortConfig) => {
      try {
        setLoading(true);

        const adjustedEndDate = new Date(currentFilters.endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);

        const params = {
          empresa_ids: companyIds,
          q: currentFilters.q,
          start_date: currentFilters.startDate,
          end_date: adjustedEndDate.toISOString().split('T')[0],
          status: currentFilters.statuses,
          sort_by: currentSortConfig.key,
          sort_direction: currentSortConfig.direction,
        };

        const response = await axios.get(API_URL, {
          params,
          paramsSerializer: params => {
            return Object.entries(params)
              .flatMap(([key, value]) =>
                Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`) : `${key}=${encodeURIComponent(value)}`
              )
              .join('&');
          }
        });
        setRequisiciones(response.data);
        setError('');
      } catch (error) {
        console.error('Error fetching requisiciones:', error);
        setError('No se pudieron cargar las requisiciones.');
      } finally {
        setLoading(false);
      }
    }, 500);

    if (location.state?.refresh) {
      fetchRequisiciones.cancel();
      fetchRequisiciones(filters, selectedCompanyIds, sortConfig);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchRequisiciones(filters, selectedCompanyIds, sortConfig);
    }

    return () => {
      fetchRequisiciones.cancel();
    };
  }, [filters, selectedCompanyIds, sortConfig, location.state, navigate, user, location.pathname]);

  const handleRowClick = (id) => {
    navigate(`/requisiciones/${id}`);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleExcelExport = async () => {
    try {
      const params = {
        empresa_ids: selectedCompanyIds,
        q: filters.q,
        start_date: filters.startDate,
        end_date: (() => {
          if (!filters.endDate) return '';
          const date = new Date(filters.endDate);
          date.setDate(date.getDate() + 1);
          return date.toISOString().split('T')[0];
        })(),
        status: filters.statuses,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await axios.get(`${API_URL}/export`, {
        params,
        responseType: 'blob',
        paramsSerializer: params => {
          return Object.entries(params)
            .flatMap(([key, value]) =>
              Array.isArray(value) ? value.map(v => `${key}=${encodeURIComponent(v)}`) : `${key}=${encodeURIComponent(value)}`
            )
            .join('&');
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'requisiciones.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      setError('No se pudo exportar a Excel.');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Requisiciones
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExcelExport}
            sx={{ mr: 2 }}
          >
            Exportar a Excel
          </Button>
          {canCreate && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/requisiciones/nueva')}
            >
              Crear Nueva Requisición
            </Button>
          )}
        </Box>
      </Box>

      {showFilters && (
        <FilterToolbar
          filters={filters}
          onFilterChange={setFilters}
          statusOptions={REQUISICION_STATUSES}
        />
      )}

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'doc_numb'}
                    direction={sortConfig.key === 'doc_numb' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('doc_numb')}
                  >
                    N° Requisición
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Artículos</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Empresa</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Creado por</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'estado'}
                    direction={sortConfig.key === 'estado' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('estado')}
                  >
                    Estado
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortConfig.key === 'created_at'}
                    direction={sortConfig.key === 'created_at' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Fecha
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Última Nota</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requisiciones.map((requisicion) => {
                const displayStatus = requisicion.status_ciclo && requisicion.status_ciclo !== 'activo'
                  ? requisicion.status_ciclo.charAt(0).toUpperCase() + requisicion.status_ciclo.slice(1)
                  : requisicion.estado;
                const chipColor = statusColors[displayStatus];
                const isCustomColor = chipColor && chipColor.startsWith('#');

                return (
                  <TableRow
                    key={requisicion.id}
                    hover
                    onClick={() => handleRowClick(requisicion.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {requisicion.doc_numb || 'Pendiente'}
                    </TableCell>
                    <TableCell>
                      {requisicion.total ? `$${requisicion.total.toFixed(2)}` : '$0.00'}
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          requisicion.detalles && requisicion.detalles.length > 0
                            ? requisicion.detalles.map(d => `${d.cantidad} x ${d.articulo}`).join(', ')
                            : 'Sin artículos'
                        }
                        arrow
                      >
                        <span>
                          {highlightText(
                            requisicion.detalles && requisicion.detalles.length > 0
                              ? requisicion.detalles[0].articulo
                              : 'Sin artículos',
                            filters.q
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{highlightText(requisicion.empresa ? requisicion.empresa.nombre : 'N/A', filters.q)}</TableCell>
                    <TableCell>{highlightText(requisicion.creador ? requisicion.creador.username : 'N/A', filters.q)}</TableCell>
                    <TableCell>{highlightText(requisicion.area || 'N/A', filters.q)}</TableCell>
                    <TableCell>
                      <Chip
                        label={displayStatus}
                        style={{ backgroundColor: isCustomColor ? chipColor : undefined, color: isCustomColor ? 'white' : undefined }}
                        color={!isCustomColor ? (chipColor || 'default') : undefined}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(requisicion.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title={requisicion.nota_preview || ''} arrow>
                        <span>{highlightText(requisicion.nota_preview || 'N/A', filters.q)}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default RequisicionList;
